'use client';

import { c as _c } from "react/compiler-runtime";
import { getLiveActivitySectionPath } from '@/app/(authenticated)/(dashboard)/live-activity/constants';
import { getSettingsSectionPath } from '@/app/(authenticated)/(dashboard)/settings/constants';
import { GetInteractionAggregatesReqFiltersItem } from '@/api/generated/model';
import { PARAMS_KEY_AGGREGATE_BY, PARAMS_KEY_HOME_TAB, PARAMS_KEY_INTERACTION_FILTERS, PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID, PARAMS_KEY_REPORT_ID, PARAMS_KEY_REPORT_SIDEBAR_SECTION, PARAMS_KEY_SUBPRODUCT, PARAMS_KEY_TIME_RANGE } from '@/constants/route-params-keys';
import type { HomeTab, NavigateAppRequest } from '@/domains/navigation/app-navigation.types';
import { resolveHomeTab } from '@/domains/navigation/app-navigation.types';
import type { NebulyPageKey } from '@/domains/pages/types';
import type { ReportSubproduct } from '@/domains/reports/utils/report-subproduct.utils';
import { getNebulyPageRoute } from '@/domains/pages/constants';
import { getNebulyPageDefinition } from '@/domains/pages/registry';
import { UsersPageData } from '@/domains/home/components/home-tabs/users/page';
import { useLocalFilters } from '@/contexts/filters/filter-context';
import { getTimeRangeConfigDefault } from '@/utils/time-range-config';
import { queryStateSerializer } from '@/utils/useQueryParamState/utils';
import { isValidUrl, sanitizeUrl } from '@/utils/url.utils';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useTransition } from 'react';
function resolveNavigatePath(request: NavigateAppRequest): string {
  switch (request.route) {
    case 'live-activity-section':
      return getLiveActivitySectionPath(request.section);
    case 'settings-section':
      return getSettingsSectionPath(request.section);
    case 'roi-setup-section':
      return `/roi-setup/${request.section}`;
    case 'admin-panel-section':
      return `/admin-panel/${request.section}`;
    default:
      return request.route;
  }
}
export const useAppNavigation = defaultHomeTab => {
  const $ = _c(35);
  const router = useRouter();
  const params = useSearchParams();
  const [, startTransition] = useTransition();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: "filter",
      defaultValue: []
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    setFilters: setInteractionFilters
  } = useLocalFilters(PARAMS_KEY_INTERACTION_FILTERS, t0);
  let t1;
  if ($[1] !== params) {
    t1 = (p, keepList) => {
      const newParams = new URLSearchParams();
      newParams.set(PARAMS_KEY_PROJECT_ID, params.get(PARAMS_KEY_PROJECT_ID));
      newParams.set(PARAMS_KEY_ORGANIZATION_ID, params.get(PARAMS_KEY_ORGANIZATION_ID));
      newParams.set(PARAMS_KEY_TIME_RANGE, params.get(PARAMS_KEY_TIME_RANGE) ?? JSON.stringify(getTimeRangeConfigDefault()));
      if (keepList) {
        keepList.forEach(k => {
          const value = params.get(k);
          if (value) {
            newParams.set(k, value);
          } else {
            newParams.delete(k);
          }
        });
      }
      const aggregateBy = params.get(PARAMS_KEY_AGGREGATE_BY);
      if (aggregateBy) {
        newParams.set(PARAMS_KEY_AGGREGATE_BY, aggregateBy);
      }
      if (p) {
        Object.keys(p).forEach(k_0 => {
          if (k_0 === PARAMS_KEY_REPORT_ID || k_0 === PARAMS_KEY_REPORT_SIDEBAR_SECTION) {
            if (p[k_0]) {
              newParams.set(k_0, p[k_0]);
            } else {
              newParams.delete(k_0);
            }
            return;
          }
          if (p[k_0]) {
            newParams.set(k_0, p[k_0]);
          } else {
            newParams.delete(k_0);
          }
        });
      }
      return newParams;
    };
    $[1] = params;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const setCommonParams = t1;
  let t2;
  if ($[3] !== router || $[4] !== setCommonParams || $[5] !== startTransition) {
    t2 = request => {
      const {
        params: routeParams,
        keepList: keepList_0,
        target: t3,
        nativeRouter: t4,
        pathname: pathnameOverride,
        before
      } = request;
      const target = t3 === undefined ? "_self" : t3;
      const nativeRouter = t4 === undefined ? false : t4;
      before?.();
      const pathname = resolveNavigatePath(request);
      const newParams_0 = setCommonParams(routeParams, keepList_0);
      const homeTab = resolveHomeTab(pathname);
      if (homeTab) {
        newParams_0.set(PARAMS_KEY_HOME_TAB, homeTab);
      }
      const destination = pathnameOverride ?? pathname;
      if (nativeRouter) {
        const url = `${destination}?${newParams_0}`;
        const domain = window.location.origin;
        if (!isValidUrl(domain + url)) {
          throw new Error("Invalid URL");
        }
        const sanitizedUrl = sanitizeUrl(url);
        if (target !== "_self") {
          window.open(sanitizedUrl, target);
          return;
        }
        window.location.href = url;
        return;
      }
      if (target !== "_self") {
        window.open(`${destination}?${newParams_0}`, target);
        return;
      }
      startTransition(() => {
        router.push(`${destination}?${newParams_0}`, {
          scroll: resolveHomeTab(pathname) ? false : undefined
        });
      });
    };
    $[3] = router;
    $[4] = setCommonParams;
    $[5] = startTransition;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const navigateApp = t2;
  let t3;
  if ($[7] !== router) {
    t3 = (projectId, organizationId) => {
      const newParams_1 = new URLSearchParams();
      newParams_1.set(PARAMS_KEY_PROJECT_ID, queryStateSerializer(projectId, "string"));
      newParams_1.set(PARAMS_KEY_ORGANIZATION_ID, queryStateSerializer(organizationId, "string"));
      router.push(`/reports?${newParams_1.toString()}`, {
        scroll: false
      });
    };
    $[7] = router;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const goToHomeWithCleanQueryParams = t3;
  let t4;
  if ($[9] !== navigateApp) {
    t4 = data => {
      if (data.page === "detail") {
        navigateApp({
          route: "live-activity-section",
          section: "cohorts",
          params: {
            cohort: queryStateSerializer(data.cohort, "string")
          }
        });
      } else {
        navigateApp({
          route: "live-activity-section",
          section: "users"
        });
      }
    };
    $[9] = navigateApp;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const goToUsers = t4;
  let t5;
  if ($[11] !== navigateApp || $[12] !== setInteractionFilters) {
    t5 = (filters, optionalParams) => {
      navigateApp({
        route: "live-activity-section",
        section: "interactions",
        before: () => setInteractionFilters(filters),
        params: optionalParams
      });
    };
    $[11] = navigateApp;
    $[12] = setInteractionFilters;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const goToInteractions = t5;
  let t6;
  if ($[14] !== navigateApp) {
    t6 = () => {
      navigateApp({
        route: "admin-panel-section",
        section: "ingestion"
      });
    };
    $[14] = navigateApp;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const goToDataManagement = t6;
  let t7;
  if ($[16] !== navigateApp) {
    t7 = () => {
      navigateApp({
        route: "admin-panel-section",
        section: "export-logs"
      });
    };
    $[16] = navigateApp;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const goToExportLogs = t7;
  let t8;
  if ($[18] !== navigateApp) {
    t8 = (reportId, subproduct) => {
      navigateApp({
        route: "/reports",
        params: {
          [PARAMS_KEY_REPORT_ID]: queryStateSerializer(reportId, "string"),
          ...(subproduct ? {
            [PARAMS_KEY_SUBPRODUCT]: subproduct
          } : {})
        },
        keepList: subproduct ? undefined : [PARAMS_KEY_SUBPRODUCT]
      });
    };
    $[18] = navigateApp;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const goToReport = t8;
  let t9;
  if ($[20] !== navigateApp) {
    t9 = (pageKey, sidebarSection) => {
      const page = getNebulyPageDefinition(pageKey);
      navigateApp({
        route: getNebulyPageRoute(pageKey),
        params: {
          [PARAMS_KEY_SUBPRODUCT]: page.subproduct,
          ...(sidebarSection ? {
            [PARAMS_KEY_REPORT_SIDEBAR_SECTION]: queryStateSerializer(sidebarSection, "string")
          } : {})
        }
      });
    };
    $[20] = navigateApp;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const goToNebulyPage = t9;
  let t10;
  if ($[22] !== defaultHomeTab || $[23] !== params) {
    t10 = params.get("homeTab") || defaultHomeTab;
    $[22] = defaultHomeTab;
    $[23] = params;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = t10 as HomeTab;
  let t12;
  if ($[25] !== goToDataManagement || $[26] !== goToExportLogs || $[27] !== goToHomeWithCleanQueryParams || $[28] !== goToInteractions || $[29] !== goToNebulyPage || $[30] !== goToReport || $[31] !== goToUsers || $[32] !== navigateApp || $[33] !== t11) {
    t12 = {
      currentTab: t11,
      navigateApp,
      goToUsers,
      goToInteractions,
      goToReport,
      goToNebulyPage,
      goToDataManagement,
      goToExportLogs,
      goToHomeWithCleanQueryParams
    };
    $[25] = goToDataManagement;
    $[26] = goToExportLogs;
    $[27] = goToHomeWithCleanQueryParams;
    $[28] = goToInteractions;
    $[29] = goToNebulyPage;
    $[30] = goToReport;
    $[31] = goToUsers;
    $[32] = navigateApp;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
};